import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/config";

const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/leads`, { name: form.name, email: form.email });
      toast.success("Access unlocked.", {
        description: "The Somatic Script Diagnostic & Blueprint is on its way to your inbox.",
      });
      setForm({ name: "", email: "" });
    } catch (err) {
      toast.error("Submission failed.", {
        description: err.response?.data?.detail?.[0]?.msg || "Please verify your details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls = "form-field w-full rounded-md px-5 py-4 text-sm font-light";

  return (
    <section id="access" className="py-24 lg:py-36 border-t border-white/10 geo-grid relative" data-testid="access-section">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#0A0A0A_80%)] pointer-events-none"
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#00FF8F] mb-6" data-testid="access-overline">
            Final Transmission
          </p>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-white font-medium" data-testid="access-heading">
            ACCESS THE SOMATIC SCRIPT
            <br />
            <span className="italic text-[#00FF8F]">DIAGNOSTIC & BLUEPRINT</span>
          </h2>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-12 rounded-lg border border-[#00FF8F]/25 bg-[#111113]/80 backdrop-blur-xl p-8 sm:p-10"
          data-testid="lead-form"
        >
          <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#00FF8F]/60 to-transparent" aria-hidden="true" />
          <div className="space-y-6">
            <div>
              <label htmlFor="lead-name" className="block font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#00FF8F] mb-3">
                Name
              </label>
              <input
                id="lead-name"
                type="text"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your full name"
                className={fieldCls}
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <label htmlFor="lead-email" className="block font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#00FF8F] mb-3">
                Email
              </label>
              <input
                id="lead-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="you@company.com"
                className={fieldCls}
                data-testid="lead-email-input"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="neon-btn mt-8 w-full rounded-full px-8 py-5 text-base font-bold uppercase tracking-[0.15em]"
            data-testid="lead-submit-button"
          >
            {submitting ? "Unlocking…" : "UNLOCK BIOLOGICAL SOVEREIGNTY ➔"}
          </button>
          <p className="mt-5 text-center text-xs text-[#6E6E76]">
            Your data remains private. The protocol is delivered directly to your inbox.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default LeadForm;
